import { useEffect, useState } from "react";
import { Sparkles, Copy, Trash2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const CATS = [
  { id: "joyeria", label: "Joyería" },
  { id: "floristeria", label: "Floristería" },
  { id: "cosmetica", label: "Cosmética" },
];

export default function AdminPostGenerator() {
  const [category, setCategory] = useState("joyeria");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [tone, setTone] = useState("lujo editorial");
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/products?category=${category}&limit=100`).then(r => setProducts(r.data));
    setProductId("");
  }, [category]);

  const loadHistory = () => api.get("/admin/ai/generated-posts").then(r => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setGenerating(true);
    setCurrent(null);
    try {
      const { data } = await api.post("/admin/ai/generate-post", {
        category, product_id: productId || null, tone, language: "español",
      });
      setCurrent(data);
      toast.success("Posts generados");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setGenerating(false); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este post generado?")) return;
    await api.delete(`/admin/ai/generated-posts/${id}`);
    loadHistory();
    if (current?.id === id) setCurrent(null);
  };

  const posts = current?.posts;

  return (
    <div data-testid="admin-post-generator">
      <p className="overline mb-3">IA · Claude Sonnet 4.5</p>
      <h1 className="font-heading text-4xl text-ivory mb-3">Generador de posts</h1>
      <p className="text-sm text-ivory/60 mb-8 max-w-2xl">Genera automáticamente posts para Instagram/Facebook, TikTok y Pinterest a partir de una categoría o producto. Texto editorial de lujo con tono M&H Lumora.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="bg-graphite border border-smoke p-6 space-y-5 h-fit">
          <h2 className="font-heading text-xl text-ivory mb-2">Configuración</h2>

          <div>
            <label className="luxe-label">Categoría</label>
            <div className="grid grid-cols-3 gap-2">
              {CATS.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`cat-${c.id}`}
                  className={`text-xs uppercase tracking-[0.15em] py-2 border ${category === c.id ? "border-gold text-gold" : "border-smoke text-ivory/60"}`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="luxe-label">Producto específico (opcional)</label>
            <select
              data-testid="product-select"
              value={productId}
              onChange={e => setProductId(e.target.value)}
              className="luxe-input border-b border-smoke"
            >
              <option value="" className="bg-carbon">— Genérico de categoría —</option>
              {products.map(p => <option key={p.id} value={p.id} className="bg-carbon">{p.name}</option>)}
            </select>
          </div>

          <div>
            <label className="luxe-label">Tono</label>
            <input data-testid="tone-input" value={tone} onChange={e => setTone(e.target.value)} className="luxe-input" />
          </div>

          <button
            onClick={generate}
            disabled={generating}
            data-testid="generate-btn"
            className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {generating ? <><RefreshCw size={14} className="animate-spin" /> Generando...</> : <><Sparkles size={14} /> Generar posts</>}
          </button>
        </div>

        {/* Result */}
        <div className="lg:col-span-2 space-y-4">
          {!posts && !generating && (
            <div className="bg-graphite border border-smoke p-12 text-center">
              <Sparkles className="text-gold mx-auto mb-4" size={32} />
              <p className="font-heading text-2xl text-ivory">Listo para generar</p>
              <p className="text-sm text-ivory/60 mt-2">Configura los parámetros y pulsa "Generar posts"</p>
            </div>
          )}

          {generating && (
            <div className="bg-graphite border border-smoke p-12 text-center">
              <RefreshCw className="text-gold mx-auto mb-4 animate-spin" size={32} />
              <p className="font-heading text-2xl text-ivory">Creando con Claude Sonnet 4.5...</p>
              <p className="text-sm text-ivory/60 mt-2">Esto puede tardar 5-15 segundos</p>
            </div>
          )}

          {posts && (
            <>
              {/* Instagram / Facebook */}
              <Panel title="Instagram · Facebook" testId="panel-instagram">
                <div className="relative">
                  <pre className="whitespace-pre-wrap text-ivory/90 text-sm leading-relaxed font-body">{posts.instagram?.caption}</pre>
                  <CopyBtn onCopy={() => copyText(posts.instagram?.caption)} />
                </div>
                {posts.instagram?.hashtags?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-smoke flex flex-wrap gap-2">
                    {posts.instagram.hashtags.map((h, i) => <span key={i} className="text-xs text-gold">{h}</span>)}
                    <button onClick={() => copyText(posts.instagram.hashtags.join(" "))} className="text-xs text-ivory/40 ml-auto hover:text-gold uppercase tracking-wider">Copiar hashtags</button>
                  </div>
                )}
              </Panel>

              {/* TikTok */}
              <Panel title="TikTok" testId="panel-tiktok">
                <p className="text-gold font-heading text-lg mb-2">Gancho:</p>
                <p className="text-ivory mb-4 italic">"{posts.tiktok?.hook}"</p>
                <p className="overline mb-2">Guion</p>
                <pre className="whitespace-pre-wrap text-ivory/90 text-sm leading-relaxed font-body">{posts.tiktok?.script}</pre>
                {posts.tiktok?.hashtags?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-smoke flex flex-wrap gap-2">
                    {posts.tiktok.hashtags.map((h, i) => <span key={i} className="text-xs text-gold">{h}</span>)}
                  </div>
                )}
                <CopyBtn onCopy={() => copyText(`${posts.tiktok?.hook}\n\n${posts.tiktok?.script}\n\n${(posts.tiktok?.hashtags || []).join(" ")}`)} />
              </Panel>

              {/* Pinterest */}
              <Panel title="Pinterest" testId="panel-pinterest">
                <p className="overline mb-1">Título SEO</p>
                <p className="text-ivory mb-4">{posts.pinterest?.title}</p>
                <p className="overline mb-1">Descripción</p>
                <pre className="whitespace-pre-wrap text-ivory/90 text-sm leading-relaxed font-body">{posts.pinterest?.description}</pre>
                {posts.pinterest?.keywords?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-smoke flex flex-wrap gap-2">
                    {posts.pinterest.keywords.map((k, i) => <span key={i} className="text-xs px-2 py-1 border border-smoke text-ivory/70">{k}</span>)}
                  </div>
                )}
                <CopyBtn onCopy={() => copyText(`${posts.pinterest?.title}\n\n${posts.pinterest?.description}`)} />
              </Panel>
            </>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-xl text-ivory mb-4">Historial ({history.length})</h3>
              <div className="space-y-2">
                {history.slice(0, 8).map(h => (
                  <div key={h.id} className="flex justify-between items-center bg-graphite border border-smoke px-4 py-3" data-testid={`history-${h.id}`}>
                    <button onClick={() => setCurrent(h)} className="text-left flex-1 text-sm">
                      <span className="text-gold uppercase tracking-wider text-xs">{h.category}</span>
                      <span className="text-ivory/40 ml-3 text-xs">{new Date(h.created_at).toLocaleString("es-ES")}</span>
                    </button>
                    <button onClick={() => del(h.id)} className="text-ivory/40 hover:text-rose"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, testId, children }) {
  return (
    <div className="bg-graphite border border-smoke p-6 relative" data-testid={testId}>
      <p className="overline mb-4">{title}</p>
      {children}
    </div>
  );
}

function CopyBtn({ onCopy }) {
  return (
    <button onClick={onCopy} className="absolute top-4 right-4 text-ivory/40 hover:text-gold" title="Copiar">
      <Copy size={14} />
    </button>
  );
}
